import { Component, OnInit, OnDestroy, NgZone } from "@angular/core";
import { Subscription } from "rxjs";

import {
  SignalRService,
  SignalREventService,
  ToastrService,
  GlobalService,
  AuthorizationService,
  AnnotationService,
  Annotation,
  ReportService,
} from "./shared/index";

@Component({
  selector: "admin-app",
  templateUrl: "./shared/master.html",
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private signalRSubscription: Subscription;
  private authorizationSubscription: Subscription;
  private versionSubscription: Subscription;
  private datasetSourceSubscription: Subscription;
  private annotationSubscription: Subscription;
  private baseAnnotationSubscription: Subscription;
  private pushSubscription: Subscription;

  public totalAnnotations = 0;
  public annotations: Annotation[] = [];

  public isLoggedIn = false;
  public isConnected = false;
  public datasetSource: "";
  public version = "";
  public iosVersion = "";
  public uwpVersion = "";
  public uwpslVersion = "";
  public andVersion = "";
  public newAnnotations = 0;
  public bUpdateFMIFTP = "false";

  public isDownloadingDocumentDetailReport = false;
  public isDownloadingFMIVerificationReport = false;

  public copyRightYear = new Date().getFullYear();

  //public lastPush: Date;
  //public lastPushUser: string;

  //@HostListener('window:beforeunload', ['$event'])
  //beforeUnloadHandler(event: any) {
  //    alert("beforeunload");
  //}
  constructor(
    private _ngZone: NgZone,
    private _signalRService: SignalRService,
    private _signalREventService: SignalREventService,
    private _toastrService: ToastrService,
    private _globalService: GlobalService,
    private _annotationService: AnnotationService,
    private _authorizationService: AuthorizationService,
    private _reportService: ReportService
  ) {}

  ngOnInit(): void {
    this.signalRSubscription = this._signalRService.connectionChange.subscribe(
      (connectionChange: boolean) => {
        this.isConnected = connectionChange;
      }
    );

    this.authorizationSubscription =
      this._authorizationService.authenticateComplete.subscribe((info: any) => {
        this.isLoggedIn = true;
        this.annotationSubscription =
          this._annotationService.newAnnotation.subscribe((info: Annotation) =>
            this.onNewAnnotation(info)
          );
        this.baseAnnotationSubscription =
          this._annotationService.annotationsCountChange.subscribe(
            (info: any) => {
              this.totalAnnotations = info;
            }
          );
        //this.pushSubscription = this._globalService.pushNoticeSent.subscribe((info: string) => this.onPushSent(info));
        this._annotationService.getAnnotationCount();
      });

    this.versionSubscription = this._globalService.versionChange.subscribe(
      (info: any) => {
        this.version = info.Data.wVersion;
        this.iosVersion = info.Data.iVersion;
        this.uwpVersion = info.Data.uVersion;
        this.uwpslVersion = info.Data.uslVersion;
        this.andVersion = info.Data.aVersion;
        this.bUpdateFMIFTP = info.Data.updateFMIFTP;
      }
    );

    this.datasetSourceSubscription =
      this._globalService.datasetSourceChange.subscribe((info: any) => {
        this.datasetSource = info.Data.datasetSource;
      });
    this._globalService.getDatasetSource();
    this._globalService.getVersion();
  }

  //20200307 mrose - remove notifications
  //public broadcastChanges(): void {
  //    this._globalService.broadcastPush();
  //}

  //private onPushSent(user: any): void {
  //    this.lastPush = new Date(Date.now());
  //    this.lastPushUser = user;
  //    this._toastrService.displayToastrMessage("success", user.FirstName + " " + user.LastName + " has sent a push notice to all devices.", "Push Notification Sent");
  //}

  private onNewAnnotation(annotation: Annotation): void {
    this._ngZone.run(() => {
      this._toastrService.displayToastrMessage(
        "success",
        "New annotation from " +
          annotation.User.FirstName +
          " " +
          annotation.User.LastName +
          " for " +
          annotation.Document.FileName +
          "!",
        "New Annotation"
      );
      if (this.annotations.length == 5) {
        this.annotations.pop();
      }
      this.annotations.unshift(annotation);
      this.newAnnotations += 1;
      this.totalAnnotations += 1;
    });
  }

  ngOnDestroy(): void {
    this.signalRSubscription.unsubscribe();
    this.versionSubscription.unsubscribe();
    this.datasetSourceSubscription.unsubscribe();
    this.authorizationSubscription.unsubscribe();
    this.annotationSubscription.unsubscribe();
    this._signalREventService.deregisterOnServerEvents();
  }

  public hasPermission(area: string): boolean {
    return this._authorizationService.hasPermission(area);
  }

  /**
   * `api/Reports/DocumentDetailReport` requires an Authorization header, which the menu's former
   * browser navigation could not supply, so the report is fetched here and saved as a blob.
   */
  public downloadDocumentDetailReport(): void {
    if (this.isDownloadingDocumentDetailReport) {
      return;
    }

    this.isDownloadingDocumentDetailReport = true;
    // The Bootstrap dropdown closes on this click, hiding the in-menu spinner, so the only
    // visible progress cue has to come from a toast.
    this._toastrService.displayToastrMessage(
      "info",
      "Building the Document Details report. The download will start when it is ready.",
      "Preparing Download"
    );
    this._reportService
      .downloadReport(this._reportService.reportUrl("DocumentDetailReport"))
      .subscribe({
        next: (response) => {
          this.isDownloadingDocumentDetailReport = false;
          this._reportService.saveAsFile(
            response,
            "ProManual-DocumentDetails.xlsx"
          );
        },
        error: (error: Error) => {
          this.isDownloadingDocumentDetailReport = false;
          this._toastrService.displayToastrMessage(
            "error",
            error.message,
            "Document Details Download Failed"
          );
        },
      });
  }

  /**
   * See {@link downloadDocumentDetailReport}. This report also re-runs the FMI verification audit
   * server-side, so it can take several minutes to come back.
   */
  public downloadFMIVerificationReport(): void {
    if (this.isDownloadingFMIVerificationReport) {
      return;
    }

    this.isDownloadingFMIVerificationReport = true;
    // See downloadDocumentDetailReport - and this one re-runs the whole FMI audit first, so the
    // wait is measured in minutes.
    this._toastrService.displayToastrMessage(
      "info",
      "Running the FMI verification audit. This can take several minutes; the download will start when it is ready.",
      "Preparing Download"
    );
    this._reportService
      .downloadReport(this._reportService.reportUrl("FMIVerificationReport"))
      .subscribe({
        next: (response) => {
          this.isDownloadingFMIVerificationReport = false;
          this._reportService.saveAsFile(
            response,
            "ProManual-FMIDocumentVerification.xlsx"
          );
        },
        error: (error: Error) => {
          this.isDownloadingFMIVerificationReport = false;
          this._toastrService.displayToastrMessage(
            "error",
            error.message,
            "FMI Verification Download Failed"
          );
        },
      });
  }
}
